import {
  Text,
} from '@chakra-ui/react';
import Chart from 'react-apexcharts';
import { selectedDatabaseIdState, currentDatabaseSelector } from '@transport-insights/uikit';
import {
  usePlanningQualityTrend,
} from '@src/modules/performance/context/reports-api-hooks';
import { useRecoilValue } from 'recoil';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Activity Management Planning',
  content: [
    {
      title: 'Planning quality trend',
      description:
  <>
    <Text fontSize="inherit" mb={2}>
      The trend in the 3-year independent assessments by Road Efficiency Group Te Ringa Maimoa of the TA&apos;s Transport Activity Management Plan (AMP) submitted to NZTA.
    </Text>
    <Text fontSize="inherit" mb={2}>
      AMPs are assessed against elements of the &quot;Road Efficiency Group Te Ringa Maimoa Pillars of Success&quot; framework including Systems, Evidence, Communicating, Decision Making, Service Delivery and Improvement Plan.
    </Text>
    <Text fontSize="inherit" mb={2}>
      Each assessment consists of 23 attributes scored 0 to 3. Each result displayed is an average of the attribute scores.
    </Text>
  </>,
      source:
  <Text fontSize="inherit" as="span">
    Assessment, results and grading provided by Road Efficiency Group Te Ringa Maimoa.
  </Text>,
    },
  ],
};

const formatNltpYear = (nltpYear) => {
  // Strip the year from the nltpYear string (e.g., "2023/24" -> "2023")
  // add on 3 years and format the result as YYYY-YY
  const startYear = parseInt(nltpYear.split('/')[0], 10);
  const endYear = (startYear + 3) % 100; // Get the last two digits of the end year
  return `${startYear}-${endYear.toString().padStart(2, '0')}`;
};

function ActivityManagementPlanning() {
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const { data, isLoading } = usePlanningQualityTrend();
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }

  const distinctYears = [...new Set(data?.map((item) => formatNltpYear(item.nltpYear)))].sort();
  const lockYear = data?.[0]?.lockYear || null;

  const chartData = [
    {
      name: currentDatabase?.name,
      data: data?.map((item) => ({ x: formatNltpYear(item.nltpYear), y: item.averagePillarsScore })),
      color: CHART_COLOURS.blue,
    },
    {
      name: data?.[0]?.regionalCouncil || 'Region',
      data: data?.map((item) => ({ x: formatNltpYear(item.nltpYear), y: item.regionalAveragePillarsScore })),
      color: CHART_COLOURS.orange,
    },
    {
      name: 'Peer Group',
      data: data?.map((item) => ({ x: formatNltpYear(item.nltpYear), y: item.peerGroupAveragePillarsScore })),
      color: CHART_COLOURS.black,
    },
    ...(!isNational
      ? [
        {
          name: 'National',
          data: data?.map((item) => ({ x: formatNltpYear(item.nltpYear), y: item.nationalAveragePillarsScore })),
          color: CHART_COLOURS.lightgrey,
        },
      ]
      : []),
  ];

  // Filter out the Peer Group and National data if it's a national report, and Peer Group if it's a region or state highways report
  const filteredChartData = filterChartDataByName(
    chartData,
    chartFilters,
  );

  return (
    <ReportSection
      title="Activity Management Planning"
      informationSource={informationSource}
      isLoading={isLoading || !data}
      lockYear={lockYear}
    >
      <Chart
        options={{
          title: {
            text: 'Planning quality trend',
          },
          chart: {
            animations: {
              enabled: false,
            },
          },
          xaxis: {
            categories: distinctYears,
            labels: {
              offsetY: 0,
              rotate: -45,
              rotateAlways: false,
            },
          },
          yaxis: [
            {
              forceNiceScale: false,
              stepSize: 0.5,
              min: 0,
              max: 3,
              labels: {
                formatter(y) {
                  return `${y.toFixed(1)}`;
                },
              },
            },
          ],
          grid: {
            padding: {
              left: 20,
            },
          },
          dataLabels: {
            enabled: false,
          },
          stroke: {
            show: false,
          },
          tooltip: {
            shared: true,
            intersect: false,
            y: {
              formatter(value) {
                return `${value.toFixed(2)}`;
              },
            },
          },
        }}
        series={filteredChartData}
        type="bar"
        width="100%"
        height="280"
      />
    </ReportSection>
  );
}

export default ActivityManagementPlanning;
